import { DownOutlined } from "@ant-design/icons"
import { Button, Flex, Tooltip } from "antd"
import { type ColumnsType } from "antd/es/table"
import type { BaseOptionType } from "antd/lib/select"
import React from "react"

import {
  ProjectStatusSelector,
  type StatusChangeOptions,
  type StatusChangeOutcome
} from "../../../Project/components/ProjectStatusSelector.tsx"
import { ProjectStatusTag } from "../../../Project/components/ProjectStatusTag.tsx"
import {
  canChangeProjectStatus,
  canCloseProject
} from "../../../Project/helpers/project-edit-visibility.ts"
import { type PermissionProjectPermissions } from "../../../SettingsPermissions/types/permissions.types.ts"
import { type ProjectListItem } from "../../types/types.ts"
import { getProjectsColumnActions } from "../components/column-actions.tsx"
import { PROJECT_COLUMN_KEYS, getColumns } from "../components/columns.tsx"
import {
  FinancialCell,
  LastActivityCell,
  ProgressCell,
  ProjectIdentityCell,
  ProjectManagerCell,
  ScheduleHealthCell
} from "./project-cells.tsx"
import { NextMilestoneCell } from "./project-cells.tsx"

/** The current list's secondary columns, kept for Table settings. */
const OPTIONAL_KEYS: string[] = [
  PROJECT_COLUMN_KEYS.uid,
  PROJECT_COLUMN_KEYS.customer,
  PROJECT_COLUMN_KEYS.endDate,
  PROJECT_COLUMN_KEYS.created,
  PROJECT_COLUMN_KEYS.createdBy
]

export const REDESIGN_DEFAULT_HIDDEN_COLUMNS = OPTIONAL_KEYS

/**
 * How much of the row fits, by the table's own width rather than the
 * viewport's, since the sidebar and the page padding move the table without
 * moving the window. Project, Status, Schedule, Project Manager, Progress and
 * Actions are always columns; the rest step aside in a fixed order.
 */
export type ProjectTableTier = "full" | "laptop" | "compact" | "narrow"

// Each floor is the sum of that tier's column widths plus the checkbox column,
// so a tier never starts out scrolling sideways.
export const tableTierFor = (width: number): ProjectTableTier =>
  width >= 1520
    ? "full"
    : width >= 1240
      ? "laptop"
      : width >= 960
        ? "compact"
        : "narrow"

/** Next milestone joins the Schedule cell from the laptop tier down. */
export const foldsMilestone = (tier: ProjectTableTier) => tier !== "full"
/** Financial moves into the row details from the compact tier down. */
export const detailsFinancial = (tier: ProjectTableTier) =>
  tier === "compact" || tier === "narrow"
/** Last activity follows it on the narrowest table. */
export const detailsActivity = (tier: ProjectTableTier) => tier === "narrow"
/** A details panel exists only once something the caller may see left the row. */
export const hasRowDetails = (tier: ProjectTableTier, showBudget: boolean) =>
  (showBudget && detailsFinancial(tier)) || detailsActivity(tier)

const WIDTHS: Record<ProjectTableTier, Record<string, number>> = {
  full: {
    project: 280,
    status: 145,
    schedule: 160,
    milestone: 200,
    pm: 200,
    financial: 140,
    progress: 170,
    activity: 130,
    actions: 64
  },
  laptop: {
    project: 225,
    status: 135,
    schedule: 200,
    milestone: 0,
    pm: 195,
    financial: 125,
    progress: 120,
    activity: 120,
    actions: 72
  },
  compact: {
    project: 210,
    status: 135,
    schedule: 190,
    milestone: 0,
    pm: 180,
    financial: 0,
    progress: 120,
    activity: 130,
    actions: 88
  },
  narrow: {
    project: 215,
    status: 125,
    schedule: 175,
    milestone: 0,
    pm: 140,
    financial: 0,
    progress: 120,
    activity: 0,
    actions: 80
  }
}

interface GetRedesignColumnsArg {
  t: (key: string, options?: Record<string, unknown>) => string
  statuses: BaseOptionType[]
  projectPermissions: PermissionProjectPermissions | undefined
  showProgress: boolean
  showBudget: boolean
  showMilestones: boolean
  editAllowed: boolean
  archiveAllowed: boolean
  deleteAllowed?: boolean
  tier?: ProjectTableTier
  expandedIds?: string[]
  onToggleDetails?: (id: string) => void
  onStatusChange: (
    id: string,
    status: number,
    options?: StatusChangeOptions
  ) => Promise<StatusChangeOutcome>
  onSelectRow: (record: ProjectListItem, action: string) => Promise<void> | void
}

export const getRedesignColumns = (
  args: GetRedesignColumnsArg
): ColumnsType<ProjectListItem> => {
  const {
    t,
    statuses,
    projectPermissions,
    showProgress,
    showBudget,
    showMilestones,
    editAllowed,
    archiveAllowed,
    deleteAllowed = false,
    tier = "full",
    expandedIds = [],
    onToggleDetails,
    onStatusChange,
    onSelectRow
  } = args
  const width = WIDTHS[tier]

  const legacy = getColumns({
    ...args,
    archived: false,
    restoreAllowed: false,
    deleteAllowed: false
  })
  const optional = legacy.filter((column) =>
    OPTIONAL_KEYS.includes(String(column.key))
  )

  const actions = getProjectsColumnActions({
    t,
    editAllowed,
    archiveAllowed,
    restoreAllowed: false,
    deleteAllowed: false,
    onSelectRow
  })
  // The Closed quick view lists archived rows: they open read-only, so they
  // offer what the Archive does — View, Restore and Delete — and no edits.
  const archivedActions = getProjectsColumnActions({
    t,
    editAllowed: false,
    archiveAllowed: false,
    restoreAllowed: archiveAllowed,
    deleteAllowed,
    onSelectRow
  })
  const renderMenu = (value: unknown, record: ProjectListItem, index: number) =>
    (record.is_archived ? archivedActions : actions).render?.(
      value,
      record,
      index
    )

  return [
    {
      title: t("projectsCommand.columns.project"),
      dataIndex: "lead_name",
      key: PROJECT_COLUMN_KEYS.leadName,
      sorter: true,
      width: width.project,
      align: "left",
      // Kept in view when a narrow table scrolls sideways.
      fixed: tier === "narrow" ? "left" : undefined,
      render: (_: unknown, project) => <ProjectIdentityCell project={project} />
    },
    {
      title: t("projectsCommand.columns.status"),
      dataIndex: "project_status",
      key: PROJECT_COLUMN_KEYS.projectStatus,
      width: width.status,
      align: "left",
      // The badge reads at body size here, as the mockup draws it.
      render: (status: string, record) => (
        <span className="[&_.ant-tag]:!px-2.5 [&_.ant-tag]:!text-[13px] [&_.ant-tag]:!leading-6">
          {!record.is_archived &&
          canChangeProjectStatus(projectPermissions, record) ? (
            <ProjectStatusSelector
              status={status}
              statuses={statuses}
              canClose={canCloseProject(projectPermissions, record)}
              onChange={(newStatus, options) =>
                onStatusChange(record?.id, newStatus, options)
              }
            />
          ) : (
            <ProjectStatusTag status={status} />
          )}
        </span>
      )
    },
    {
      title: t("projectsCommand.columns.schedule"),
      key: PROJECT_COLUMN_KEYS.scheduleHealth,
      width: width.schedule,
      align: "left",
      render: (_: unknown, project) => (
        <ScheduleHealthCell
          project={project}
          withNextMilestone={showMilestones && foldsMilestone(tier)}
        />
      )
    },
    {
      hidden: !showMilestones || foldsMilestone(tier),
      title: t("projectsCommand.columns.nextMilestone"),
      key: PROJECT_COLUMN_KEYS.nextMilestone,
      width: width.milestone,
      align: "left",
      render: (_: unknown, project) => <NextMilestoneCell project={project} />
    },
    {
      title:
        tier === "narrow" ? (
          <abbr
            title={t("projectsCommand.columns.projectManager")}
            className="no-underline"
          >
            {t("projectsCommand.columns.pm")}
          </abbr>
        ) : (
          t("projectsCommand.columns.projectManager")
        ),
      dataIndex: "project_managers",
      key: PROJECT_COLUMN_KEYS.projectManagers,
      sorter: true,
      width: width.pm,
      align: "left",
      render: (_: unknown, project) => <ProjectManagerCell project={project} />
    },
    {
      hidden: !showBudget || detailsFinancial(tier),
      title: t("projectsCommand.columns.financial"),
      dataIndex: "sales_price",
      key: PROJECT_COLUMN_KEYS.salesPrice,
      sorter: true,
      width: width.financial,
      align: "right",
      render: (_: unknown, project) => <FinancialCell project={project} />
    },
    {
      hidden: !showProgress,
      title: t("projectsCommand.columns.progress"),
      dataIndex: "progress",
      key: PROJECT_COLUMN_KEYS.progress,
      width: width.progress,
      align: "left",
      render: (_: unknown, project) => <ProgressCell project={project} />
    },
    {
      hidden: detailsActivity(tier),
      title: t("projectsCommand.columns.lastActivity"),
      dataIndex: "updated",
      key: PROJECT_COLUMN_KEYS.lastActivity,
      sorter: true,
      width: width.activity,
      align: "left",
      render: (_: unknown, project) => <LastActivityCell project={project} />
    },
    ...optional,
    {
      ...actions,
      // The shared 60px clips its own header label.
      title: t("projectsCommand.columns.actions"),
      width: width.actions,
      render: (value: unknown, record: ProjectListItem, index: number) => {
        const menu = renderMenu(value, record, index) as React.ReactNode
        if (!hasRowDetails(tier, showBudget) || !onToggleDetails) return menu
        const open = expandedIds.includes(record.id)
        const label = t(
          open
            ? "projectsCommand.details.hide"
            : "projectsCommand.details.show",
          { name: record.lead_name }
        )
        return (
          <Flex gap={4} justify="center" align="center">
            <Tooltip title={label}>
              <Button
                type="text"
                size="small"
                aria-expanded={open}
                aria-label={label}
                onClick={() => onToggleDetails(record.id)}
                icon={
                  <DownOutlined
                    className={
                      open
                        ? "rotate-180 transition-transform"
                        : "transition-transform"
                    }
                  />
                }
              />
            </Tooltip>
            {menu}
          </Flex>
        )
      }
    }
  ]
}
